import { injectable } from 'inversify';
import { validateOrReject, validate, ValidationError } from 'class-validator';
import { plainToClass } from 'class-transformer';
import { FieldValidator } from 'final-form';
import at from 'lodash/at';
import intersection from 'lodash/intersection';
import get from 'lodash/get';

import { FileType } from '../file-type';
import { AppError } from '../error';

type AllStrings<T> = {
  [P in keyof T]?: T[P] extends (infer U)[] ? AllStrings<U>[] : string;
};

type NumberValidator = {
  type: 'number';
  less_than?: number;
  less_than_or_equal?: number;
  greater_than?: number;
  greater_than_or_equal?: number;
  attribute_id: string;
};

interface FileInputValue {
  value: string | File;
  source: string | File;
}

interface UrlSource {
  type: 'url';
  value: string; // server url
}

interface LocalSource {
  type: 'local';
  value: string; // localCacheId
}

type Source = UrlSource | LocalSource;

interface ServerValue {
  // JSON.stringify({ type: 'file', id: attribute.value.id })
  type: 'server';
  value: string;
}

interface CacheValue {
  // JSON.stringify({ type: 'file_cache', id: cacheId, expiredAt: cacheExpiredAt || '' })
  type: 'cache';
  value: string;
}

interface LocalValue {
  // localCacheId
  type: 'local';
  value: string;
}

type Value = ServerValue | CacheValue | LocalValue;

interface BaseMeta {
  mimeType: string;
  size: number;
  localCacheId: string;
  name: string;
}

interface GeneralMeta extends BaseMeta {
  type: 'general';
}

interface ImageMeta extends BaseMeta {
  type: 'image';
  width: number;
  height: number;
}

type FileMeta = GeneralMeta | ImageMeta;

interface FileInputValueNext {
  value: Value;
  source: Source;
  meta?: FileMeta;
}

interface Condition {
  attribute_id: string;
  equal?: string[];
  unequal?: string[];
}

const NUMBER_B_IN_MB = 1048576;
const DEFAULT_SIZE_LIMIT = 20;
const MAX_HOURS = 23;
const MAX_MINUTES = 59;
const EXCLUDE_EXTENSION = ['jfif'];
const ALLOWED_CHARACTERS_REGEXP =
  /^[A-Za-zА-Яа-яЁё0-9 !#$%&'()*+,\-./:;=?@[\\\]^_`{|}~№«»–—]*$/;

const getImageResolution = (
  file: File,
): Promise<{ width: number; height: number }> => {
  const image: Promise<{ width: number; height: number }> = new Promise(
    (resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          resolve({ width: img.width, height: img.height });
        };
        img.onerror = reject;
        img.src = reader.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    },
  );

  return image;
};

const getFileExtension = (fileName: string) => {
  const lastDotIndex = fileName.lastIndexOf('.');

  return lastDotIndex > 0 ? fileName.slice(lastDotIndex + 1).toLowerCase() : '';
};

const validateFile = async (
  file: File,
  message: string,
  fileTypes: string[],
  fileTypeChecker: FileType,
  skipFileTypeValidation?: boolean,
  maxSize?: number,
  // eslint-disable-next-line max-params
) => {
  const extension = getFileExtension(file.name);

  if (EXCLUDE_EXTENSION.includes(extension)) {
    return message;
  }

  let fileType = skipFileTypeValidation
    ? file.type
    : await fileTypeChecker.fileTypeFromBlob(file);

  if (file?.name.includes('.sig')) {
    fileType = 'application/pgp-signature';
  } else if (file?.name.includes('.p7m')) {
    fileType = 'application/pkcs7-mime';
  }

  return !fileTypes.includes(fileType) ||
    file.size > (maxSize ?? DEFAULT_SIZE_LIMIT) * NUMBER_B_IN_MB
    ? message
    : undefined;
};

const validateFileNext = async (
  fileMeta: FileMeta,
  message: string,
  fileTypes: string[],
  maxSize?: number,
) => {
  const extension = getFileExtension(fileMeta.name);

  if (EXCLUDE_EXTENSION.includes(extension)) {
    return message;
  }

  let fileType = fileMeta.mimeType;

  if (fileMeta.name.includes('.sig')) {
    fileType = 'application/pgp-signature';
  } else if (fileMeta.name.includes('.p7m')) {
    fileType = 'application/pkcs7-mime';
  }

  return !fileTypes.includes(fileType) ||
    fileMeta.size > (maxSize ?? DEFAULT_SIZE_LIMIT) * NUMBER_B_IN_MB
    ? message
    : undefined;
};

const errorsToAllStrings = <T>(
  value: T,
  errors: ValidationError[],
): AllStrings<T> => {
  const result = Array.isArray(value)
    ? ([] as (typeof value)[0])
    : ({} as Record<keyof T, unknown>);

  if (!errors || !errors.length) return result;

  errors.forEach(({ property, constraints, children }) => {
    if (!constraints && children && children?.length !== 0) {
      const propertyName: keyof T = property as keyof T;

      const childrenResult = errorsToAllStrings<T[typeof propertyName]>(
        value[propertyName],
        children,
      );

      result[property] = childrenResult;
    } else {
      result[property as keyof T] = constraints
        ? constraints[Object.keys(constraints)[0]]
        : 'Value is invalid';
    }
  });

  return result;
};

@injectable()
export class ValidationService {
  constructor(private readonly fileType: FileType) {}

  /**
   * Проверить объект на соответствие валидационной схеме и в случае несоответствия выбросить исключение
   *
   * @param dto - Валидируемый объект
   * @param constructor - Класс, описывающий валидационную схему
   * @param groups - Массив групп, которые встречаются в валидационной схеме
   */
  async validateOrReject<T extends object>(
    dto: object,
    constructor?: new () => T,
    groups?: string[],
  ): Promise<void> {
    try {
      const value = constructor ? plainToClass(constructor, dto) : dto;

      await validateOrReject(value, { groups });
    } catch (err) {
      throw new AppError('client', {
        code: 400,
        message: 'Bad Request',
        error: 'Bad Request',
      });
    }
  }

  /**
   * Проверить объект на соответствие валидационной схеме и вернуть объект с ошибками
   *
   * @typeParam T - Тип объекта
   * @param dto - Валидируемый объект
   * @param constructor - Класс, описывающий валидационную схему
   * @param groups - Массив групп, которые встречаются в валидационной схеме
   * @returns Объект с ошибками валидации
   */
  async validate<T extends object>(
    dto: object,
    constructor?: new () => T,
    groups?: string[],
  ): Promise<AllStrings<T>> {
    const value = constructor ? plainToClass(constructor, dto) : dto;

    const errors = await validate(value, {
      validationError: { target: false, value: false },
      groups,
    });

    const result = errorsToAllStrings<T>(value as T, errors);

    return result;
  }

  /**
   * Создать функцию для Wysiwyg, которая в случае "пустого" значения выведет заданное сообщение об ошибке
   *
   * @param message - Сообщение об ошибке в случае, если значение "пустое"
   * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
   */
  requiredWysiwyg =
    (message = 'Обязательное поле') =>
    (value?: { hasContent: boolean } | null) =>
      typeof value === 'undefined' || value === null || !value?.hasContent
        ? message
        : undefined;

  /**
   * Создать функцию, которая в случае "пустого" значения выведет заданное сообщение об ошибке
   *
   * @typeParam FieldType - Тип значения поля
   * @param message - Сообщение об ошибке в случае, если значение "пустое"
   * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
   */
  required =
    <FieldType>(message = 'Обязательное поле') =>
    (value: FieldType) =>
      typeof value === 'undefined' ||
      value === null ||
      (typeof value === 'boolean' && value === false) ||
      (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray(value) && value.length === 0)
        ? message
        : undefined;

  /**
   * Определить является ли текущее валидируемое поле обязательным
   * Текущее валидируемое поле зависит от другого поля (определено в options.field).
   * Если значение поля field (из options.field) не присутствует в in (из options.in),
   * то текущее валидируемое поле является обязательным, иначе необязательным.
   *
   * @typeParam T - Тип значения поля field
   * @typeParam FieldType - Тип валидируемого поля
   * @param message - Сообщение об ошибке
   * @param options - Параметры зависимости: поле, массив значений и тип операции
   * @returns Функция валидатора final-form
   */
  requiredIfNotIn =
    <T, FieldType>(
      message = 'Обязательное поле',
      options: { field: string; in: T[]; operation: 'in' | 'ex' },
    ): FieldValidator<FieldType> =>
    (value, allValues) => {
      const relatedAttributeValue = at(
        allValues as Record<string, unknown>,
        options.field,
      )[0] as T;

      const valueToCompare = Array.isArray(relatedAttributeValue)
        ? relatedAttributeValue
        : [relatedAttributeValue];

      const isIncludes = !!intersection(options.in, valueToCompare).length;

      return allValues &&
        relatedAttributeValue &&
        (options.operation === 'ex' ? isIncludes : !isIncludes)
        ? this.required(message)(value)
        : undefined;
    };

  /**
   * Определить является ли текущее валидируемое поле обязательным
   * Текущее валидируемое поле зависит от значений других полей (определены в options.conditions).
   * Поле является обязательным, когда все значения других полей (из options.conditions)
   * соответствуют значениям из equal или не соответствуют значениям unequal,
   * определенным для каждого поля
   *
   * @typeParam FieldType - Тип валидируемого поля
   * @param message - Сообщение об ошибке
   * @param options - Параметры зависимости: префикс и условия валидации
   * @returns Функция валидатора final-form
   */
  requiredIfMeetsConditions =
    <FieldType>(
      message = 'Обязательное поле',
      options: {
        prefix: string;
        conditions: Condition[];
      },
    ): FieldValidator<FieldType> =>
    (value, allValues) => {
      const meetsConditions = options?.conditions.every((condition) => {
        const fieldValue = at(
          allValues as Record<string, unknown>,
          `${options.prefix}_attribute_${condition.attribute_id}`,
        )[0];
        const fieldValueArr = Array.isArray(fieldValue)
          ? fieldValue
          : [fieldValue];

        if (condition.equal) {
          return condition.equal.every((item) => fieldValueArr.includes(item));
        } else if (condition.unequal) {
          return condition.unequal.every(
            (item) => !fieldValueArr.includes(item),
          );
        }
      });

      return allValues && meetsConditions
        ? this.required(message)(value)
        : undefined;
    };

  /**
   * Создать функцию, которая будет возвращать предупреждающее сообщение, если заданное строковое значение дублируется
   *
   * @param message - Сообщение, указывающее, что значение дублируется
   * @param options - Опции, содержат список для проверки на дубликаты
   * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
   */
  notInList =
    (
      message = 'Такое название уже существует',
      options: { field: string; notIn: string[] },
    ) =>
    (value: unknown) => {
      if (typeof value !== 'string') {
        return undefined;
      }

      const sameRole = options.notIn.find(
        (name) => name.toLowerCase() === value.toLowerCase(),
      );

      return sameRole ? message : undefined;
    };

  /**
   * Создать функцию, которая будет возвращать предупреждающее сообщение,
   * если заданное значение не относится к допустимым типам файлов или файл превышает допустимый размер
   *
   * @param message - Сообщение, указывающее, что значение имеет недопустимый тип или размер
   * @param options - Опции, содержат список допустимых типов
   * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
   */
  requiredIfFile = (
    message = 'Обязательное поле',
    options: {
      field: string;
      fileTypes: string[];
      skipFileTypeValidation?: boolean;
      maxSize?: number;
    },
  ) => {
    const fileTypeChecker = this.fileType;

    return async function validateValue(
      value: unknown,
    ): Promise<string | undefined> {
      if (
        !value ||
        typeof value === 'string' ||
        typeof (value as FileInputValue)?.source === 'string'
      )
        return undefined;

      if (Array.isArray(value)) {
        const res = await value.reduce(
          async (error: Promise<string | undefined>, arrItem) => {
            return (await error) || (await validateValue(arrItem));
          },
          Promise.resolve(undefined),
        );

        return res;
      }

      const result = await validateFile(
        ((value as FileInputValue)?.source as File) || (value as File),
        message,
        options.fileTypes,
        fileTypeChecker,
        options.skipFileTypeValidation,
        options.maxSize,
      );

      return result;
    };
  };

  requiredIfFileNext = (
    message = 'Обязательное поле',
    options: { field: string; fileTypes: string[]; maxSize?: number },
  ) => {
    return async function validateValue(
      value: FileInputValueNext,
    ): Promise<string | undefined> {
      if (Array.isArray(value)) {
        const res = await value.reduce(
          async (error: Promise<string | undefined>, arrItem, index) => {
            const name = arrItem.meta?.name || index;
            const err = await error;
            const validationError = await validateValue(arrItem);

            if (validationError) {
              if (err) {
                return `${err}, ${name}`;
              }

              return `${validationError}: ${name}`;
            }

            return err;
          },
          Promise.resolve(undefined),
        );
        return res;
      }

      if (
        !value ||
        typeof value === 'string' ||
        value?.value?.type === 'server' ||
        value?.source?.type !== 'local' ||
        !value?.meta
      )
        return undefined;

      const result = await validateFileNext(
        value.meta as FileMeta,
        message,
        options.fileTypes,
        options.maxSize,
      );

      return result;
    };
  };

  maxValue = (max: number, message: string) => (value: unknown) =>
    Number(value) > Number(max) ? message : undefined;

  minValue = (min: number, message: string) => (value: unknown) =>
    Number(value) < min ? message : undefined;

  /**
   * Определить попадает ли значение в заданный диапазон
   *
   * @param min - Минимальное значение диапазона
   * @param max - Максимальное значение диапазона
   * @param message - Сообщение об ошибке в случае, если значение не попадает в заданный диапазон
   * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
   */
  rangeValue =
    (min: number, max: number, message: string) => (value: unknown) =>
      Number(value) < min || Number(value) > max ? message : undefined;

  /**
   * Создать функцию, которая будет возвращать предупреждающее сообщение, если переданное значение превышает указанный в опциях лимит
   *
   * @typeParam T - Тип объекта значений
   * @typeParam K - Ключ объекта
   * @param message - Сообщение, указывающее, что значение превышает лимит
   * @param options - Опции, содержат лимит
   * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
   */
  sizeLimit = <T, K extends keyof T = keyof T>(
    message = 'Объем файла слишком большой',
    options: { field: string; sizeLimit: number },
  ) =>
    function validateValue(
      value?: T[K] | FileInputValue,
      allValues?: Partial<T>,
    ): string | undefined {
      const sizeLimit = options.sizeLimit * NUMBER_B_IN_MB;

      if (
        !value ||
        typeof value === 'string' ||
        typeof (value as FileInputValue)?.source === 'string'
      )
        return undefined;

      if (Array.isArray(value)) {
        const res = value.reduce((error: string | undefined, arrItem) => {
          return error || validateValue(arrItem, allValues);
        }, undefined);
        return res;
      } else {
        const currentValueInForm = at(allValues, options.field)[0];
        if (currentValueInForm) {
          return currentValueInForm &&
            ((value as FileInputValue)?.source as File)?.size > sizeLimit
            ? message
            : undefined;
        } else {
          return ((value as FileInputValue).source as File)?.size > sizeLimit
            ? message
            : undefined;
        }
      }
    };

  sizeLimitNext = (
    message = 'Объем файла слишком большой',
    options: { field: string; sizeLimit: number },
  ): FieldValidator<FileInputValueNext> =>
    function validateValue(value, allValues): string | undefined {
      const sizeLimit = options.sizeLimit * NUMBER_B_IN_MB;
      if (Array.isArray(value)) {
        const res = value.reduce(
          (error: string | undefined, arrItem, index) => {
            const name = arrItem.meta?.name || index;
            const validationError = validateValue(arrItem, allValues);

            if (validationError) {
              if (error) {
                return `${error}, ${name}`;
              }

              return `${validationError}: ${name}`;
            }

            return error;
          },
          undefined,
        );
        return res;
      }

      if (
        !value ||
        typeof value === 'string' ||
        value?.value?.type === 'server' ||
        value?.source?.type !== 'local' ||
        !value?.meta
      )
        return undefined;

      const currentValueInForm = at(
        allValues as Record<string, unknown>,
        options.field,
      )[0];
      if (currentValueInForm) {
        const size = value?.meta?.size || 0;
        const res =
          currentValueInForm && size > sizeLimit ? message : undefined;

        return res;
      } else {
        const size = value?.meta?.size || 0;
        return size > sizeLimit ? message : undefined;
      }
    };

  /**
   * Проверяет, что ширина и высота каждого файла не меньше заданных значений.
   * При несоответствии возвращает сообщение об ошибке для первого невалидного файла.
   *
   * @param message - Сообщение, добавляемое к ошибке, если разрешение недостаточно
   * @param options - Минимально допустимые размеры
   * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
   */
  imageResolutionCheck = (
    message: string,
    options: { minWidth: number; minHeight: number },
  ) => {
    return async function validateValue(
      value?: FileInputValue[] | FileInputValue,
    ): Promise<string | undefined> {
      if (
        !value ||
        typeof value === 'string' ||
        typeof (value as FileInputValue)?.source === 'string'
      ) {
        return undefined;
      }

      if (Array.isArray(value)) {
        const res = await value.reduce<Promise<string | undefined>>(
          (error, arrItem) => {
            return error || validateValue(arrItem);
          },
          Promise.resolve(undefined),
        );
        return res;
      } else {
        const { width, height } = await getImageResolution(
          value.source as File,
        );

        if (width < options.minWidth) return message;
        if (height < options.minHeight) return message;
      }
    };
  };

  /**
   * Создать функцию, которая будет возвращать предупреждающее сообщение, если переданное значение превышает указанный в опциях лимит
   *
   * @param options - Опции, содержат attributeId (id поля) и опционально числовой валидатор
   * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
   */
  moreOrLess =
    (options: {
      field: string;
      attributeId: string;
      validator: NumberValidator;
    }) =>
    (value?: string | undefined) => {
      if (!value) {
        return undefined;
      }

      const isNumber = /^\d+(\.\d+)?$/.test(value);

      const numberWithPoint = !isNaN(+value) && String(value).includes('.');

      if (!isNumber && !numberWithPoint) {
        return 'Данные должны быть числовым значением';
      }

      if (options.validator.greater_than) {
        const numberValue = Number(value);
        return numberValue > options.validator.greater_than
          ? undefined
          : `Число должно быть больше чем ${options.validator.greater_than}`;
      }

      if (options.validator.greater_than_or_equal) {
        const numberValue = Number(value);
        return numberValue >= options.validator.greater_than_or_equal
          ? undefined
          : `Число должно быть больше или равно ${options.validator.greater_than_or_equal}`;
      }

      if (options.validator.less_than) {
        const numberValue = Number(value);
        return numberValue < options.validator.less_than
          ? undefined
          : `Число должно быть меньше чем ${options.validator.less_than}`;
      }

      if (options.validator.less_than_or_equal) {
        const numberValue = Number(value);
        return numberValue <= options.validator.less_than_or_equal
          ? undefined
          : `Число должно быть меньше или равно ${options.validator.less_than_or_equal}`;
      }
    };

  /**
   * Проверяет, что значение является корректным URL с разрешённым протоколом
   *
   * @param message - Сообщение об ошибке в случае, если значение не является корректным URL или протокол не входит в разрешённый список
   * @param allowedProtocols - Массив разрешённых протоколов. По умолчанию: http:, https:, mailto:, tel:, sms:
   * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
   */
  validUrl =
    (
      message = 'Значение поля должно быть ссылкой',
      allowedProtocols: readonly string[] = [
        'http:',
        'https:',
        'mailto:',
        'tel:',
        'sms:',
      ],
    ) =>
    (value: string | undefined) => {
      if (!value) return undefined;

      try {
        const parsedUrl = new URL(value);
        if (!allowedProtocols.includes(parsedUrl.protocol)) {
          return message;
        }
        return undefined;
      } catch (_) {
        return message;
      }
    };

  maxLength = (max: number, message: string) => (value: unknown) => {
    if (!value) return undefined;

    return String(value).length > max ? message : undefined;
  };

  minLength = (min: number, message: string) => (value: unknown) => {
    if (!value) return undefined;

    return String(value).length < Number(min) ? message : undefined;
  };

  checkLength =
    (min: number, max: number, message: string) => (value: unknown) => {
      if (!value) return undefined;

      return String(value).length < Number(min) || String(value).length > max
        ? message
        : undefined;
    };

  /**
   * Проверяет, что значение содержит только разрешённые символы (белый список):
   * кириллица, латиница, цифры, пробел, печатные символы
   *
   * @param message - Сообщение об ошибке
   * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
   */
  allowedCharacters = (message: string) => (value: unknown) => {
    if (!value) return undefined;

    return ALLOWED_CHARACTERS_REGEXP.test(String(value)) ? undefined : message;
  };

  dateNotInPast =
    (message: string, minutes?: string) => (value: string | undefined) => {
      if (!value) return undefined;

      let combinedDateTime = new Date(`${value}T23:59`);

      if (minutes && new Date(`${value}T${minutes}`) > combinedDateTime) {
        const combinedDateTimeString = `${value}T${minutes}`;
        combinedDateTime = new Date(combinedDateTimeString);
      }

      return combinedDateTime < new Date() ? message : undefined;
    };

  timeNotInPast =
    (message: string, fieldName: string): FieldValidator<string | undefined> =>
    (value, allValues) => {
      const date = get(allValues, fieldName);

      if (!value || new Date(`${date}T23:59`) < new Date()) return undefined;

      const today = new Date();

      let combinedDateTime = new Date(`${today.toDateString()} ${value}:59`);

      if (date && new Date(date) >= combinedDateTime) {
        const combinedDateTimeString = `${date}T${value}`;
        combinedDateTime = new Date(combinedDateTimeString);
      }

      return combinedDateTime < today ? message : undefined;
    };

  isValidTime =
    (message: string) =>
    (value: string | undefined = ''): string | undefined => {
      const [hoursStr = '', minutesStr = ''] = value.split(':');

      const hours = Number(hoursStr);
      const minutes = Number(minutesStr);

      const invalid =
        isNaN(hours) ||
        isNaN(minutes) ||
        hours > MAX_HOURS ||
        minutes > MAX_MINUTES;

      return invalid ? message : undefined;
    };

  /**
   * Проверяет, является ли текущее поле обязательным, если другое поле активно.
   *
   * @param message - Сообщение об ошибке, если проверка не пройдена.
   * @param dependentFieldName - Имя поля, определяющее, является ли данное поле обязательным.
   * @returns Функция валидации, возвращающая сообщение об ошибке или undefined.
   */
  requiredIfDependentFieldExists =
    (message: string, dependentFieldName: string) =>
    (value: string | undefined, allValues?: object) => {
      const dependentFieldValue = (allValues as Record<string, unknown>)?.[
        dependentFieldName
      ];

      if (dependentFieldValue && !value) {
        return message;
      }

      return undefined;
    };

  /**
   * Создать функцию валидации количества элементов в массиве
   *
   * @param message - Сообщение об ошибке
   * @param options - Параметры валидации
   * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
   */
  arrayLength =
    (message: string, options: { min?: number; max?: number }) =>
    (value: unknown[] | undefined) => {
      if (value === undefined || value === null) {
        return undefined;
      }

      if (Array.isArray(value)) {
        const length = value.length;

        if (options.min !== undefined && length < options.min) {
          return message;
        }

        if (options.max !== undefined && length > options.max) {
          return message;
        }
      }

      return undefined;
    };

  /**
   * Определить является ли текущее валидируемое поле больше чем значение из передаваемого в options поля
   * Текущее валидируемое поле зависит от другого поля (определено в options.field).
   *
   * @typeParam FieldType - Тип валидируемого поля
   * @param message - Сообщение об ошибке
   * @param options - Параметры зависимости
   * @returns Функция валидатора final-form
   */
  maxValueWithConnectedFieldValidator =
    <FieldType>(
      message = 'Обязательное поле',
      options: { field: string },
    ): FieldValidator<FieldType> =>
    (value, allValues) => {
      const connectedValue = at(
        allValues as Record<string, unknown>,
        options.field,
      )[0];

      return Number(value) > Number(connectedValue) ? message : undefined;
    };

  /**
   * Создать функцию, которая будет возвращать предупреждающее сообщение,
   * если в тексте отсутствует поддерживаемый протокол
   *
   * @param message - Сообщение об ошибке
   * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
   */
  containsSupportedProtocol =
    (message = 'Ссылка должна содержать поддерживаемый протокол') =>
    (value: string | undefined) => {
      if (!value) return undefined;

      const SUPPORTED_PROTOCOLS = [
        'http:',
        'https:',
        'mailto:',
        'sms:',
        'tel:',
      ];

      try {
        const parsedUrl = new URL(value);
        const hasSupportedProtocol = SUPPORTED_PROTOCOLS.includes(
          parsedUrl.protocol,
        );

        if (!hasSupportedProtocol) {
          return message;
        }

        if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') {
          const hasDoubleSlash = value.includes('://');
          return hasDoubleSlash
            ? undefined
            : 'Ссылка должна содержать // после протокола';
        }

        return undefined;
      } catch {
        return message;
      }
    };

  /* eslint-disable no-magic-numbers */
  /**
   * Проверяет значение по алгоритму Луна (валидация номера банковской карты)
   *
   * @param message - Сообщение об ошибке
   * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
   */
  luhnAlgorithm = (message: string) => (value: string | undefined) => {
    if (!value) return undefined;

    if (!/^\d+$/.test(value)) {
      return message;
    }

    const cleaned = value.replace(/[\s-]/g, '');
    const reversed = cleaned.split('').reverse();
    let total = 0;

    for (let i = 0; i < reversed.length; i++) {
      let digit = parseInt(reversed[i]);

      if (i % 2 === 1) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }

      total += digit;
    }

    return total % 10 === 0 ? undefined : message;
  };
  /* eslint-enable no-magic-numbers */
}
